<script setup>
import DashboardCard from "@/components/MyStudents/Display/DashboardCard.vue";
import { useSessionV2Filters } from "@/composables/useSessionV2Filters.js";
import { computed, ref, watch, reactive, defineExpose, toRefs } from "vue";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { SESSION_FILTER_TABS } from "./constants.js";
import BookIcon from "@/components/shared/icons/BookIcon.vue";
import MarketplaceIcon from "@/components/shared/icons/MarketplaceIcon.vue";
import EyeSlashIcon from "@/components/shared/icons/EyeSlashIcon.vue";
import { buildParamsNoHits } from "./utils.js"

const props = defineProps({
  onFilterUpdate: {
    type: Function,
    required: true,
  },
  searchClient: {
    type: Object,
    required: true,
  },
  algoliaIndex: {
    type: String,
    required: true,
  },
});

const {
  mySessionsFilter,
  catalogSessionsFilter,
  hiddenSessionsFilter,
  selectedEntityId,
  selectedEntity,
} = useSessionV2Filters();

const activeTab = ref(SESSION_FILTER_TABS.MY_SESSIONS);

const onTabSelect = (newTab) => {
  activeTab.value = newTab;
}

const activeFilter = computed(() => {
  if (activeTab.value === SESSION_FILTER_TABS.MY_SESSIONS) {
    return mySessionsFilter.value;
  }
  if (activeTab.value === SESSION_FILTER_TABS.CATALOG_SESSIONS) {
    return catalogSessionsFilter.value;
  }
  if (activeTab.value === SESSION_FILTER_TABS.HIDDEN_SESSIONS) {
    return hiddenSessionsFilter.value;
  }
});

watch(activeFilter, (newFilter) => {
  props.onFilterUpdate(newFilter);
}, { immediate: true });

const tabCounts = reactive({
  mySessions: 0,
  catalogSessions: 0,
  hiddenSessions: 0,
});

const fetchTabCounts = (helper) => {
  if (!helper || !selectedEntityId.value) return;

  const searchParams = helper.state.setQueryParameter('filters', '');

  const queriesMap = {
    mySessions: {
      indexName: props.algoliaIndex,
      params: buildParamsNoHits(searchParams.setQueryParameter('filters', mySessionsFilter.value)),
    },
    catalogSessions: {
      indexName:props.algoliaIndex,
      params: buildParamsNoHits(searchParams.setQueryParameter('filters', catalogSessionsFilter.value)),
    },
    hiddenSessions: {
      indexName: props.algoliaIndex,
      params: buildParamsNoHits(searchParams.setQueryParameter('filters', hiddenSessionsFilter.value)),
    },
  };

  const queryKeys = Object.keys(queriesMap);
  const queries = queryKeys.map(key => queriesMap[key]);

  props.searchClient.multipleQueries(queries).then(({ results }) => {
    results.forEach((result, index) => {
      const key = queryKeys[index];
      tabCounts[key] = result.nbHits;
    });
  })
  .catch(error => {
    const context = {
      algoliaIndex: props.algoliaIndex,
      filters: {
        mySessions: mySessionsFilter.value,
        catalogSessions: catalogSessionsFilter.value,
        hiddenSessions: hiddenSessionsFilter.value,
      },
    };
    datadogLogs.logger.error(
      "Error fetching counts for Program Manager Sessions Index : ",
      error,
      context
    )
  });
};

const dashboardCards = computed(() => [
  {
    count: tabCounts.mySessions,
    description: `${selectedEntity?.value?.name || ''} Sessions`,
    testId: 'my-sessions-card',
    tab: SESSION_FILTER_TABS.MY_SESSIONS,
    icon: BookIcon,
  },
  {
    count: tabCounts.catalogSessions,
    description: 'Catalog Sessions',
    testId: 'catalog-sessions-card',
    tab: SESSION_FILTER_TABS.CATALOG_SESSIONS,
    icon: MarketplaceIcon,
  },
  {
    count: tabCounts.hiddenSessions,
    description: 'Hidden Sessions',
    testId: 'hidden-sessions-card',
    tab: SESSION_FILTER_TABS.HIDDEN_SESSIONS,
    icon: EyeSlashIcon,
  },
]);

defineExpose({
  fetchTabCounts,
});
</script>

<template>
  <div class="card flex flex-wrap gap-4 w-full pt-4">
    <DashboardCard
      v-for="card in dashboardCards"
      :key="card.tab"
      :number="card.count"
      :description="card.description"
      :data-testid="card.testId"
      class="flex-1 cursor-pointer"
      :class="{ 'bg-white': activeTab !== card.tab }"
      @click="onTabSelect(card.tab)"
    >
      <template #icon>
        <component :is="card.icon" class="h-5 w-5 text-university-primary" />
      </template>
    </DashboardCard>
  </div>
</template>
